import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import api from '../services/api';

const TYPE_COLORS = {
  'Truck': '#3b82f6',
  'Ocean Import': '#0891b2',
  'Ocean Export': '#7c3aed',
  'Air Import': '#ea580c',
  'Misc': '#6b7280',
  'Invoice': '#059669',
  'Cost': '#dc2626',
  'D/C Note': '#d97706',
  'Trade Partner': '#8b5cf6',
};

const STATUS_COLORS = {
  'Delivered': { bg: '#d1fae5', color: '#059669' },
  'In Transit': { bg: '#fef3c7', color: '#d97706' },
  'Pending': { bg: '#e0e7ff', color: '#4338ca' },
  'Active': { bg: '#d1fae5', color: '#059669' },
  'Paid': { bg: '#d1fae5', color: '#059669' },
  'Draft': { bg: '#f1f5f9', color: '#64748b' },
  'Open': { bg: '#dbeafe', color: '#2563eb' },
  'Posted': { bg: '#dbeafe', color: '#2563eb' },
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('All');

  useEffect(() => {
    if (query.length >= 2) {
      doSearch(query);
    }
  }, [query]);

  const doSearch = async (q) => {
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  // Group results by type for filter tabs
  const types = ['All', ...new Set(results.map(r => r.type))];
  const filtered = filterType === 'All' ? results : results.filter(r => r.type === filterType);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 4px 0' }}>
          <FiSearch style={{ marginRight: '8px', verticalAlign: '-2px' }} />
          Search Results
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          {loading ? 'Searching...' : `${results.length} result(s) for "${query}"`}
        </p>
      </div>

      {/* Filter tabs */}
      {results.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {types.map(t => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              style={{
                padding: '5px 14px', borderRadius: '16px', fontSize: '12px', fontWeight: 600,
                border: filterType === t ? '2px solid #3b82f6' : '1px solid #d1d5db',
                background: filterType === t ? '#eff6ff' : '#fff',
                color: filterType === t ? '#2563eb' : '#64748b',
                cursor: 'pointer',
              }}
            >
              {t}
              <span style={{ marginLeft: '4px', fontSize: '11px', opacity: 0.7 }}>
                ({t === 'All' ? results.length : results.filter(r => r.type === t).length})
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Results table */}
      {filtered.length > 0 ? (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', width: '110px' }}>Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left' }}>File No / Name</th>
                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Party</th>
                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Reference</th>
                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Details</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', width: '100px' }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', width: '80px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const sc = STATUS_COLORS[item.status] || { bg: '#f1f5f9', color: '#64748b' };
                return (
                  <tr
                    key={`${item.type}-${item.id}-${idx}`}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => navigate(item.url)}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '4px',
                        backgroundColor: (TYPE_COLORS[item.type] || '#6b7280') + '15',
                        color: TYPE_COLORS[item.type] || '#6b7280',
                        whiteSpace: 'nowrap',
                      }}>
                        {item.type}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>{item.file_no}</td>
                    <td style={{ padding: '10px 14px', color: '#475569' }}>{item.party || '-'}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '12px' }}>{item.ref || '-'}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '12px' }}>{item.extra || '-'}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {item.status && (
                        <span style={{
                          fontSize: '11px', padding: '3px 10px', borderRadius: '10px',
                          backgroundColor: sc.bg, color: sc.color, fontWeight: 600,
                        }}>
                          {item.status}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(item.url); }}
                        style={{
                          padding: '4px 12px', backgroundColor: '#3b82f6', color: '#fff',
                          border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : !loading && query.length >= 2 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
          <FiSearch size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>No results found for "{query}"</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>Try searching by file number, container number, customer name, MBL, HBL, or vendor name</p>
        </div>
      ) : null}
    </div>
  );
};

export default SearchResults;
